import type {
  AlgorithmConfig,
  CircuitBreakerStatus,
  SnapTradeConnection,
  Trade,
  TradeAggregate,
} from '@trading-app/shared';
import { apiClient } from './api-client';
import { debugLogger } from '@/lib/debug-logger';

// Re-export so existing consumers (e.g. RecentActivity, WebhookStocksPage)
// can keep importing these types from `services/trading`.
export type { AlgorithmConfig, CircuitBreakerStatus, Trade, TradeAggregate };

export interface TradeRequest {
  accountId: string;
  ticker: string;
  universalSymbolId: string;
  symbolDescription?: string;
  action: 'BUY' | 'SELL';
  units: number;
  orderType?: string;
  timeInForce?: string;
  price?: number;
}

export interface SymbolSearchResult {
  id: string;
  symbol: string;
  description: string;
  currency?: { code: string; name: string };
  exchange?: { code: string; name: string };
  type?: { code: string; description: string };
}

export class TradingService {

  /**
   * Initiate SnapTrade connection
   */
  async connectSnapTrade(): Promise<{ redirectUrl: string; isExistingUser: boolean }> {
    const response = await apiClient.post('/api/auth/connect-snaptrade');
    return apiClient.handleResponse(response);
  }

  /**
   * Get current SnapTrade connection status
   */
  async getSnapTradeStatus(): Promise<SnapTradeConnection> {
    const response = await apiClient.get('/api/auth/snaptrade-status');
    const data = await apiClient.handleResponse(response);
    return {
      isConnected: data.isConnected,
      connectionStatus: data.connectionStatus,
      lastConnectedAt: data.lastConnectedAt ? new Date(data.lastConnectedAt) : undefined,
      errorMessage: data.errorMessage,
    };
  }

  /**
   * Sync user connections from SnapTrade
   */
  async syncConnections(): Promise<any> {
    const response = await apiClient.post('/api/trading/sync-connections');
    return apiClient.handleResponse(response);
  }

  /**
   * Get all user connections from database
   */
  async getUserConnections(): Promise<any> {
    const response = await apiClient.get('/api/trading/connections');
    return apiClient.handleResponse(response);
  }

  /**
   * Remove a brokerage connection (soft-delete)
   */
  async removeConnection(connectionId: string): Promise<void> {
    const response = await apiClient.delete(`/api/trading/connections/${connectionId}`);
    await apiClient.handleResponse(response);
  }

  /**
   * Get dashboard data (status + connections in one call)
   */
  async getDashboardData(): Promise<{
    status: {
      isConnected: boolean;
      connectionStatus: string;
      errorMessage: string | null;
      lastConnectedAt?: string;
    };
    connections: any[];
    totalConnections: number;
    activeConnections: number;
  }> {
    const response = await apiClient.get('/api/trading/dashboard-data');
    return apiClient.handleResponse(response);
  }

  /**
   * Search for symbols using the SnapTrade API
   * Uses master credentials - no account context needed
   */
  async searchSymbol(ticker: string): Promise<{ symbols: SymbolSearchResult[] }> {
    const response = await apiClient.post('/api/trading/search-symbol', {
      ticker,
    });
    return apiClient.handleResponse(response);
  }

  /**
   * Place a trade order
   */
  async placeTrade(tradeRequest: TradeRequest): Promise<{ trade: any }> {
    debugLogger.info('PlaceTrade', 'Initiating trade request', {
      accountId: tradeRequest.accountId,
      ticker: tradeRequest.ticker,
      action: tradeRequest.action,
      units: tradeRequest.units,
      orderType: tradeRequest.orderType,
      universalSymbolId: tradeRequest.universalSymbolId
    });

    try {
      debugLogger.debug('PlaceTrade', 'Sending API request', {
        endpoint: '/api/trading/place-trade',
        payload: tradeRequest
      });

      const response = await apiClient.post('/api/trading/place-trade', tradeRequest);

      debugLogger.debug('PlaceTrade', 'Received API response', {
        status: response.status,
        ok: response.ok
      });

      const result = await apiClient.handleResponse(response);

      debugLogger.info('PlaceTrade', 'Trade successfully placed', {
        tradeId: result.trade?.id,
        status: result.trade?.status
      });

      return result;
    } catch (error: any) {
      debugLogger.error('PlaceTrade', 'Trade failed', {
        error: error.message,
        accountId: tradeRequest.accountId,
        ticker: tradeRequest.ticker
      });
      throw error;
    }
  }

  /**
   * Get user's trade history (individual orders - includes open and closed)
   */
  async getTradeHistory(accountId?: string, limit = 500): Promise<Trade[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    params.append('limit', limit.toString());

    const response = await apiClient.get(`/api/trading/trade-history?${params.toString()}`);
    const data = await apiClient.handleResponse(response);
    return data.trades || [];
  }

  /**
   * Get trade aggregates (closed trades with P&L) for performance metrics
   */
  async getTradeAggregates(params?: {
    accountId?: string;
    ticker?: string;
    algorithm?: string;
    timeRange?: 'today' | 'week' | 'month' | 'year' | 'all';
    limit?: number;
  }): Promise<TradeAggregate[]> {
    const searchParams = new URLSearchParams();
    if (params?.accountId) searchParams.append('accountId', params.accountId);
    if (params?.ticker) searchParams.append('ticker', params.ticker);
    if (params?.algorithm) searchParams.append('algorithm', params.algorithm);
    if (params?.timeRange) searchParams.append('timeRange', params.timeRange);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const response = await apiClient.get(`/api/trading/trade-aggregates?${searchParams.toString()}`);
    const data = await apiClient.handleResponse(response);
    return data.aggregates || [];
  }

  /**
   * Get open position aggregates (trades with status='OPEN')
   * Single source of truth for open positions across all views
   */
  async getOpenAggregates(): Promise<TradeAggregate[]> {
    const response = await apiClient.get('/api/trades/aggregates?status=OPEN');
    const data = await apiClient.handleResponse(response);
    return data.aggregates || [];
  }

  /**
   * Annie Mode Phase 8 (PR #152): head-to-head Mode Standings for the dashboard widget.
   */
  async getModeStandings(period: 'today' | 'week' | 'month' | 'all' = 'all'): Promise<{
    period: 'today' | 'week' | 'month' | 'all';
    eric: { netPnl: number; tradeCount: number; wins: number; avgPnl: number };
    annie: { netPnl: number; tradeCount: number; wins: number; avgPnl: number };
    leader: 'ERIC' | 'ANNIE' | 'TIE';
  }> {
    const response = await apiClient.get(`/api/trading/mode-standings?period=${period}`);
    return apiClient.handleResponse(response);
  }

  /**
   * Annie Mode Phase 8 (PR #152): readiness gate for mode toggle UI.
   */
  async getModeReadiness(): Promise<{
    ericReady: true;
    annieReady: boolean;
    annieReason?: string;
  }> {
    const response = await apiClient.get('/api/users/me/mode-readiness');
    return apiClient.handleResponse(response);
  }

  /**
   * Annie Mode Phase 8 (PR #152): flip user.active_mode. Throws on 409 annie_not_ready.
   */
  async updateActiveMode(mode: 'ERIC' | 'ANNIE'): Promise<{ success: true; activeMode: 'ERIC' | 'ANNIE' }> {
    const response = await apiClient.patch('/api/users/me/active-mode', { mode });
    if (response.status === 409) {
      const body = await response.json().catch(() => ({}));
      const err = new Error(body?.detail ?? 'Annie mode not ready') as Error & { reason?: string };
      err.reason = body?.reason ?? 'annie_not_ready';
      throw err;
    }
    return apiClient.handleResponse(response);
  }

  /**
   * Get today's closed trade aggregates (for dashboard Today's Trades)
   * Uses server-side Pacific timezone filtering
   */
  async getTodayClosedAggregates(): Promise<TradeAggregate[]> {
    const response = await apiClient.get('/api/trades/aggregates?status=CLOSED&timeRange=today');
    const data = await apiClient.handleResponse(response);
    return data.aggregates || [];
  }

  /**
   * Configure algorithm for automated trading
   */
  async configureAlgorithm(
    accountId: string,
    algorithmType: string,
    units: number,
    mode?: 'ERIC' | 'ANNIE',
  ): Promise<AlgorithmConfig> {
    const body: Record<string, unknown> = { accountId, algorithmType, units };
    if (mode) body.mode = mode;
    const response = await apiClient.post('/api/trading/algorithm/configure', body);
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Get algorithm configurations
   */
  async getAlgorithmConfigs(accountId?: string): Promise<AlgorithmConfig[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);

    const response = await apiClient.get(`/api/trading/algorithm/config?${params.toString()}`);
    const data = await apiClient.handleResponse(response);
    return data.configs || [];
  }

  /**
   * Toggle algorithm configuration active status
   */
  async toggleAlgorithmConfig(configId: string): Promise<{ id: string; isActive: boolean }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/toggle`);
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Toggle closeDaily flag for algorithm configuration
   */
  async toggleCloseDaily(configId: string): Promise<{ id: string; closeDaily: boolean }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/toggle-close-daily`);
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Update buy restriction time for algorithm configuration
   */
  async updateBuyRestriction(configId: string, minutes: number): Promise<{ id: string; buyRestrictionMinutes: number }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/buy-restriction`, { minutes });
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Update hold limit minutes for algorithm configuration
   * Auto-sells positions held longer than X minutes (0 = disabled)
   */
  async updateHoldLimit(configId: string, minutes: number): Promise<{ id: string; holdLimitMinutes: number }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/hold-limit`, { minutes });
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Delete algorithm configuration
   */
  async deleteAlgorithmConfig(configId: string): Promise<void> {
    const response = await apiClient.delete(`/api/trading/algorithm/${configId}`);
    await apiClient.handleResponse(response);
  }

  /**
   * Get circuit breaker status for algorithm
   */
  async getCircuitBreakerStatus(configId: string): Promise<CircuitBreakerStatus> {
    const response = await apiClient.get(`/api/trading/algorithm/${configId}/circuit-breaker-status`);
    const data = await apiClient.handleResponse<{ status: CircuitBreakerStatus }>(response);
    return data.status;
  }

  /**
   * Manually reset circuit breaker
   */
  async resetCircuitBreaker(configId: string): Promise<void> {
    const response = await apiClient.post(`/api/trading/algorithm/${configId}/reset-circuit-breaker`);
    await apiClient.handleResponse(response);
  }

  /**
   * Manually trigger circuit breaker for ALL configs (emergency stop)
   */
  async triggerCircuitBreakerAll(): Promise<{ triggeredCount: number; totalConfigs: number; message: string }> {
    const response = await apiClient.post('/api/trading/trigger-circuit-breaker-all');
    return apiClient.handleResponse(response);
  }

  /**
   * Reset circuit breaker for ALL configs (resume trading)
   */
  async resetCircuitBreakerAll(): Promise<{ resetCount: number; totalConfigs: number; message: string }> {
    const response = await apiClient.post('/api/trading/reset-circuit-breaker-all');
    return apiClient.handleResponse(response);
  }

  /**
   * Enable or disable midnight PT auto-reset for ALL of the user's configs.
   * When enabled (default), a tripped circuit breaker clears automatically at midnight PT.
   * When disabled, the block persists until the user manually taps "Resume Trading."
   */
  async setCircuitBreakerAutoReset(enabled: boolean): Promise<{ success: boolean; enabled: boolean; updatedCount: number }> {
    const response = await apiClient.patch('/api/trading/circuit-breaker-auto-reset', { enabled });
    return apiClient.handleResponse(response);
  }

  /**
   * Read the regime-guard override state + the current market regime.
   * When enabled, webhook BUYs keep trading through a cautious/adverse regime.
   */
  async getRegimeBypass(): Promise<{ success: boolean; enabled: boolean; regime: 'favorable' | 'cautious' | 'adverse' | null }> {
    const response = await apiClient.get('/api/trading/regime-bypass');
    return apiClient.handleResponse(response);
  }

  /**
   * Enable or disable the regime-guard override on ALL of the user's configs.
   * When enabled, BUYs are no longer paused by a cautious/adverse market regime
   * (other gates still apply). An explicit opt-out of a safety feature.
   */
  async setRegimeBypass(enabled: boolean): Promise<{ success: boolean; enabled: boolean; updatedCount: number }> {
    const response = await apiClient.patch('/api/trading/regime-bypass', { enabled });
    return apiClient.handleResponse(response);
  }

  /**
   * Update daily loss limit percentage
   */
  async updateDailyLossLimit(configId: string, limitPct: number): Promise<{ id: string; dailyLossLimitPct: number }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/daily-loss-limit`, { limitPct });
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Update daily profit target for circuit breaker
   * @param configId Algorithm config ID
   * @param limit Dollar amount (0 = disabled)
   */
  async updateDailyProfitLimit(configId: string, limit: number): Promise<{ id: string; dailyProfitLimit: number }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/daily-profit-limit`, { limit });
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Update circuit breaker block mode
   * @param configId Algorithm config ID
   * @param mode 'buy_only' or 'all'
   */
  async updateBlockMode(configId: string, mode: 'buy_only' | 'all'): Promise<{ id: string; circuitBreakerBlockMode: string }> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/block-mode`, { mode });
    const data = await apiClient.handleResponse(response);
    return data.config;
  }

  /**
   * Update daily profit limit on ALL of the user's configs (global sync).
   * Used by the Settings UI to present profit target as a global setting.
   */
  async updateDailyProfitLimitAll(configs: AlgorithmConfig[], limit: number): Promise<void> {
    await Promise.all(configs.map(config => this.updateDailyProfitLimit(config.id, limit)));
  }

  /**
   * Update block mode on ALL of the user's configs (global sync).
   */
  async updateBlockModeAll(configs: AlgorithmConfig[], mode: 'buy_only' | 'all'): Promise<void> {
    await Promise.all(configs.map(config => this.updateBlockMode(config.id, mode)));
  }

  /**
   * Get webhook-traded stocks with stats
   */
  async getWebhookStocks(): Promise<WebhookStock[]> {
    const response = await apiClient.get('/api/trading/webhook-stocks');
    const data = await apiClient.handleResponse(response);
    return data.stocks || [];
  }

  /**
   * Block or unblock a ticker from webhook trading
   */
  async blockStock(configId: string, ticker: string, blocked: boolean): Promise<void> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/block-ticker`, {
      ticker,
      blocked,
    });
    await apiClient.handleResponse(response);
  }

  /**
   * Set or remove per-ticker unit override for webhook trading
   * @param configId Algorithm config ID
   * @param ticker Stock ticker
   * @param units Number of units to trade (null to remove override)
   */
  async updateTickerUnits(configId: string, ticker: string, units: number | null): Promise<void> {
    const response = await apiClient.put(`/api/trading/algorithm/${configId}/ticker-units`, {
      ticker,
      units,
    });
    await apiClient.handleResponse(response);
  }

  /**
   * Get algorithm performance statistics from trade_aggregates
   */
  async getAggregateStats(
    accountId?: string,
    ticker?: string,
    algorithm?: string,
    timeRange?: 'today' | 'week' | 'month' | 'year' | 'all'
  ): Promise<AlgorithmStats[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (ticker) params.append('ticker', ticker);
    if (algorithm) params.append('algorithm', algorithm);
    if (timeRange) params.append('timeRange', timeRange);

    const response = await apiClient.get(`/api/trading/aggregate-stats?${params.toString()}`);
    const data = await apiClient.handleResponse(response);
    return data.stats || [];
  }

  // ── Regime endpoints ────────────────────────────────────────────

  async getCurrentRegime(): Promise<{
    regime: 'favorable' | 'cautious' | 'adverse' | null;
    tradingDate: string | null;
    probability: number;
    observationCount: number;
    insufficient: boolean;
    regimeSince: string | null;
    previousRegime: 'favorable' | 'cautious' | 'adverse' | null;
  }> {
    const response = await apiClient.get('/api/regimes/current');
    return apiClient.handleResponse(response);
  }

  async getRegimeHistory(startDate?: string, endDate?: string): Promise<{
    regimes: Array<{
      tradingDate: string;
      regime: 'favorable' | 'cautious' | 'adverse';
      probability: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString();
    const response = await apiClient.get(`/api/regimes/history${qs ? `?${qs}` : ''}`);
    return apiClient.handleResponse(response);
  }

  async recalculateRegimes(): Promise<{ success: boolean; current: any }> {
    const response = await apiClient.post('/api/regimes/recalculate');
    return apiClient.handleResponse(response);
  }

  async getRegimePnL(): Promise<{
    periods: Array<{
      startDate: string;
      endDate: string;
      regime: 'favorable' | 'cautious' | 'adverse';
      netPnl: number;
    }>;
  }> {
    const response = await apiClient.get('/api/regimes/pnl');
    return apiClient.handleResponse(response);
  }
}

export interface WebhookStock {
  ticker: string;
  accountId: string;
  accountName: string;
  brokerageName: string;
  algorithmType: string;
  configId: string;
  isConfigActive: boolean;
  currentPosition: number;
  totalTrades: number;
  lastTradeDate: string | null;
  totalRealizedPnl: number;
  winRate: number;
  totalWins: number;
  totalLosses: number;
  isBlocked: boolean;
  unitOverride: number | null;  // Per-ticker override (null if using default)
  defaultUnits: number;         // Algorithm's default units
}

export interface AlgorithmStats {
  algorithmType: string;
  totalTrades: number;
  closedTrades: number;
  winRate: number;
  totalPnL: number;
  wins: number;
  losses: number;
  avgWin: number;
  avgLoss: number;
  bestTrade: number;
  worstTrade: number;
}

export const tradingService = new TradingService();